import type { ReactElement } from "react";
import { Field, QtyInput, StatusBadge } from "@cx/ui";
import type { ProductLotsView } from "@cx/contracts";
import { MAX_LOT_CODE_LENGTH, formatDateBR, formatQty } from "@cx/domain";
import { LOT_STATUS_LABELS, LOT_STATUS_TONES, type LotDraft } from "./labels";

/**
 * Lote de uma entrada (RF-SUP-07): código (nLote da NF-e, até 20) e datas opcionais. Lote que já existe no produto é
 * reaproveitado pelo código; as datas só completam o que faltar.
 */
export function LotFields({
  value,
  onChange,
  required,
  error,
}: {
  value: LotDraft;
  onChange(v: LotDraft): void;
  required?: boolean;
  error?: string | null;
}): ReactElement {
  return (
    <div className="row g-2">
      <Field label="Lote" required={required} className="col-md-4" error={error ?? undefined}>
        <input
          value={value.code}
          maxLength={MAX_LOT_CODE_LENGTH}
          onChange={(e) => onChange({ ...value, code: e.target.value })}
        />
      </Field>
      <Field label="Fabricação" className="col-md-4">
        <input
          type="date"
          value={value.manufacturedAt}
          onChange={(e) => onChange({ ...value, manufacturedAt: e.target.value })}
        />
      </Field>
      <Field label="Validade" className="col-md-4">
        <input
          type="date"
          value={value.expiresAt}
          onChange={(e) => onChange({ ...value, expiresAt: e.target.value })}
        />
      </Field>
    </div>
  );
}

type WarehouseLots = ProductLotsView["warehouses"][number];

/**
 * Escolha manual dos lotes numa saída: quantidade por lote do depósito. Tudo vazio = automático (o "sem lote" primeiro,
 * depois o que vence antes).
 */
export function LotTakesFields({
  lots,
  value,
  onChange,
  problem,
}: {
  lots: WarehouseLots | undefined;
  value: Record<string, number | null>;
  onChange(v: Record<string, number | null>): void;
  problem: string | null;
}): ReactElement {
  const rows = (lots?.lots ?? []).filter((l) => l.physicalQ4 > 0);
  return (
    <fieldset className="col-12 mb-3">
      <legend className="h6 fs-6">Lotes da saída</legend>
      <p className="text-muted small mb-2">
        Deixe em branco para a escolha automática: primeiro o que está sem lote, depois o lote que vence
        antes.
      </p>
      {rows.length === 0 ? (
        <p className="text-muted small mb-0">Nenhum lote com saldo neste depósito.</p>
      ) : (
        <table className="table table-sm align-middle mb-1" aria-label="Lotes da saída">
          <thead>
            <tr>
              <th scope="col">Lote</th>
              <th scope="col">Validade</th>
              <th scope="col" className="text-end">
                Saldo
              </th>
              <th scope="col" style={{ width: "8rem" }} className="text-end">
                Sair
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.lotId}>
                <td>{l.code}</td>
                <td>
                  {l.expiresAt ? formatDateBR(l.expiresAt) : "—"}{" "}
                  {l.status !== "valid" && (
                    <StatusBadge tone={LOT_STATUS_TONES[l.status]}>{LOT_STATUS_LABELS[l.status]}</StatusBadge>
                  )}
                </td>
                <td className="text-end qty">{formatQty(l.physicalQ4)}</td>
                <td>
                  <QtyInput
                    aria-label={`Quantidade do lote ${l.code}`}
                    className="form-control form-control-sm text-end"
                    value={value[l.lotId] ?? null}
                    onChange={(q) => onChange({ ...value, [l.lotId]: q })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {problem && (
        <p className="text-danger small mb-0" role="alert">
          {problem}
        </p>
      )}
    </fieldset>
  );
}
